import { useState } from "react";
import { ArrowRight, CheckCircle2, Circle, Rocket, X } from "lucide-react";
import { Link } from "react-router-dom";

import type { DashboardResponse } from "@/api/dashboard";
import { OverviewQuickActions } from "@/features/dashboard/components/OverviewQuickActions";
import { RecentActivityCard } from "@/features/dashboard/components/RecentActivityCard";

type Props = {
  dashboard: DashboardResponse;
  firstName: string;
  canConfigure: boolean;
  userId: string;
  churchId: string;
};

export function OnboardingDashboard({ dashboard, firstName, canConfigure, userId, churchId }: Props) {
  const onboarding = dashboard.onboarding;
  const storageKey = `ministryiq:onboarding-welcome:${churchId}:${userId}`;
  const [welcomeOpen, setWelcomeOpen] = useState(
    () => canConfigure && localStorage.getItem(storageKey) !== "dismissed",
  );
  const percentage = Math.round(
    onboarding.total_task_count ? (onboarding.completed_task_count / onboarding.total_task_count) * 100 : 0,
  );

  function dismissWelcome() {
    localStorage.setItem(storageKey, "dismissed");
    setWelcomeOpen(false);
  }

  return (
    <>
      {welcomeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="welcome-title">
          <div className="w-full max-w-lg rounded-3xl border border-indigo-500/30 bg-slate-900 p-7 shadow-2xl">
            <button type="button" onClick={dismissWelcome} aria-label="Close welcome" className="float-right rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"><X size={18} /></button>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-500/15 text-indigo-300"><Rocket size={24} /></div>
            <h2 id="welcome-title" className="mt-5 text-2xl font-bold text-white">Welcome to {dashboard.church_name}, {firstName}</h2>
            <p className="mt-3 leading-7 text-slate-300">MinistryIQ will guide you through the essential setup steps. Progress updates automatically as your church adds real data.</p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link to={onboarding.next_recommended_action?.to ?? "/"} onClick={dismissWelcome} className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-500 px-5 py-3 font-semibold text-white hover:bg-indigo-400">Start Setup <ArrowRight size={17} /></Link>
              <button type="button" onClick={dismissWelcome} className="rounded-xl border border-slate-700 px-5 py-3 font-semibold text-slate-200 hover:bg-slate-800">Explore Instead</button>
            </div>
            <p className="mt-4 text-xs text-slate-500">The setup checklist remains available on your dashboard.</p>
          </div>
        </div>
      )}

      <section className="rounded-3xl border border-indigo-500/20 bg-gradient-to-br from-slate-900 to-indigo-950/60 p-7 text-white">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-300">Church setup</p>
        <h1 className="mt-2 text-3xl font-bold">Good to see you, {firstName}</h1>
        <p className="mt-2 max-w-2xl text-slate-300">{dashboard.church_name} is {onboarding.maturity === "EMPTY" ? "ready for its first setup steps" : "building the history needed for engagement intelligence"}.</p>
      </section>

      <div className="mt-7 grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
        <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6">
          <div className="flex items-end justify-between gap-4"><div><h2 className="text-xl font-semibold text-white">Setup checklist</h2><p className="mt-1 text-sm text-slate-400">{onboarding.completed_task_count} of {onboarding.total_task_count} complete</p></div><span className="text-2xl font-bold text-indigo-300">{percentage}%</span></div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-800"><div className="h-full rounded-full bg-indigo-500 transition-all" style={{ width: `${percentage}%` }} /></div>
          <div className="mt-5 space-y-3">
            {onboarding.tasks.map((task) => (
              <Link key={task.key} to={task.to} className="flex items-start gap-3 rounded-xl border border-slate-800 p-4 transition hover:border-indigo-500/40 hover:bg-slate-800/60">
                {task.completed ? <CheckCircle2 className="mt-0.5 shrink-0 text-emerald-400" size={20} /> : <Circle className="mt-0.5 shrink-0 text-slate-500" size={20} />}
                <span className="flex-1"><span className={`block font-semibold ${task.completed ? "text-slate-400 line-through" : "text-white"}`}>{task.title}</span><span className="mt-1 block text-sm text-slate-500">{task.description}</span></span><ArrowRight className="mt-1 text-slate-500" size={17} />
              </Link>
            ))}
          </div>
        </section>

        <div className="space-y-6">
          <section className="rounded-2xl border border-indigo-500/25 bg-indigo-500/10 p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-300">What&apos;s next</p>
            <h2 className="mt-3 text-xl font-semibold text-white">{onboarding.next_recommended_action?.title ?? "Setup complete"}</h2>
            <p className="mt-2 text-sm leading-6 text-slate-300">{onboarding.next_recommended_action?.description ?? "Your church is ready to use the full executive dashboard."}</p>
            {onboarding.next_recommended_action && <Link to={onboarding.next_recommended_action.to} className="mt-5 inline-flex items-center gap-2 font-semibold text-indigo-300 hover:text-indigo-200">Continue setup <ArrowRight size={17} /></Link>}
          </section>
          {!onboarding.executive_analytics_ready && <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6"><h2 className="font-semibold text-white">Intelligence is warming up</h2><p className="mt-2 text-sm leading-6 text-slate-400">Church Health and trend insights become meaningful after attendance has been recorded on at least four service dates.</p></section>}
          <OverviewQuickActions />
        </div>
      </div>

      {dashboard.recent_events.length > 0 && <div className="mt-7"><RecentActivityCard events={dashboard.recent_events.slice(0, 6)} /></div>}
    </>
  );
}
